// src/components/Home.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import mixpanel from "mixpanel-browser";
import "../css/okgather1.webflow.css";
import { captureTrafficSource, captureFbcId } from '../utils/sourceTracking';
import Footer from "./Footer";
import { trackPageView, setUserIdProperty, recordPageView } from "../utils/tracking";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({
  publicKey: currentKey,
});

const Home = () => {
  const navigate = useNavigate();

  // User state
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Button styles (from old version)
  const buttonStyles = {
    background: "linear-gradient(135deg, #8B5FBF, #2c2e96)",
    color: "#fff",
    padding: "24px 70px",
    borderRadius: "50px",
    textDecoration: "none",
    fontSize: "1.4rem",
    fontWeight: "700",
    transition: "all 0.3s ease",
    boxShadow: "0 8px 30px rgba(139, 95, 191, 0.35), 0 4px 15px rgba(139, 95, 191, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
    display: "inline-block",
    cursor: "pointer",
    border: "none",
    transform: "translateY(0)",
    position: "relative",
    overflow: "hidden",
    letterSpacing: "0.5px",
    minWidth: "280px",
    textAlign: "center",
  };

  const buttonHoverStyles = {
    background: "linear-gradient(135deg, #9D6FD1, #2c2e96)",
    transform: "translateY(-4px) scale(1.02)",
    boxShadow: "0 15px 45px rgba(139, 95, 191, 0.5), 0 8px 20px rgba(139, 95, 191, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
  };

  // Facebook click ID capture
  useEffect(() => {
    captureFbcId();
  }, []);

  // Check authentication and subscription status
  useEffect(() => {
    (async () => {
      try {
        const currentMember = await memberstack.getCurrentMember();
        if (currentMember?.data) {
          setIsLoggedIn(true);
          const hasActivePlan = currentMember.data.planConnections.some(plan => {
            const status = plan.status?.toLowerCase();
            const type = plan.type?.toLowerCase();
            return (
              type === "subscription" &&
              (status === "active" || status === "trialing")
            );
          });

          if (hasActivePlan) {
            navigate("/my-events");
          } else {
            navigate("/events");
          }
        }
      } catch (err) {
        console.error("Memberstack DOM error:", err);
        setIsLoggedIn(false);
      }
    })();
  }, [navigate]);


  // Track homepage view. captureTrafficSource() is first-touch (written
  // once to localStorage and never overwritten) so this event - and the
  // 'user_source' value later sent at signup/checkout - stay consistent
  // even on a repeat visit with a different (or no) referrer.
  useEffect(() => {
    setUserIdProperty(); // Set user ID if logged in

    const source = captureTrafficSource();
    trackPageView('Home Page', {
      is_logged_in: isLoggedIn,
      traffic_source: source,
      referrer: document.referrer
    });
    recordPageView({ landingPage: 'homepage', source });
  }, [isLoggedIn]);

  // URL redirects
  useEffect(() => {
    let shouldRedirect = false;
    let newUrl = window.location.href;

    if (
      window.location.protocol === 'http:' &&
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1'
    ) {
      newUrl = newUrl.replace('http:', 'https:');
      shouldRedirect = true;
    }

    if (window.location.hostname === 'www.okgather.com') {
      newUrl = newUrl.replace('www.okgather.com', 'okgather.com');
      shouldRedirect = true;
    }

    if (shouldRedirect) {
      window.location.replace(newUrl);
    }
  }, []);

  return (
    <div className="page-wrapper" style={{ minHeight: "100vh", backgroundColor: "#FAFAFA" }}>
      <main className="main-wrapper">
        {/* Top Navigation Banner */}
        <section
          className="nav-banner"
          style={{
            background: "linear-gradient(135deg, #FBFAFF, #F5F2FF)",
            borderBottom: "1px solid rgba(139, 95, 191, 0.08)",
            position: "sticky",
            top: 0,
            zIndex: 100,
          }}
        >
          <div className="padding-global">
            <div className="container-large">
              <div
                className="nav-content"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "18px 0",
                }}
              >
                <span
                  className="nav-logo"
                  style={{
                    fontSize: "2rem",
                    fontWeight: "800",
                    background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    margin: 0,
                    lineHeight: "44px",
                    display: "inline-block",
                  }}
                >
                  OkGather
                </span>

                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  {isLoggedIn ? (
                    <>
                      <a
                        href="/edit-profile"
                        style={{
                          background: "transparent",
                          color: "#8B5FBF",
                          padding: "8px 16px",
                          borderRadius: "20px",
                          textDecoration: "none",
                          fontSize: "0.9rem",
                          fontWeight: "600",
                          border: "2px solid #8B5FBF",
                          transition: "all 0.3s ease",
                        }}
                      >
                        My Profile
                      </a>
                    </>
                  ) : (
                    <>
                      <a
                        href="/events"
                        className="nav-signup-button"
                        style={{
                          background: "linear-gradient(135deg, #8B5FBF, #2c2e96)",
                          color: "#fff",
                          padding: "12px 24px",
                          borderRadius: "25px",
                          textDecoration: "none",
                          fontSize: "0.95rem",
                          fontWeight: "600",
                          transition: "all 0.3s ease",
                          boxShadow: "0 2px 10px rgba(139, 95, 191, 0.2)",
                          border: "none",
                        }}
                      >
                        Join Us
                      </a>
                      <a
                        href="/login"
                        className="nav-login-button"
                        style={{
                          background: "transparent",
                          color: "#8B5FBF",
                          padding: "12px 24px",
                          borderRadius: "25px",
                          textDecoration: "none",
                          fontSize: "0.95rem",
                          fontWeight: "600",
                          transition: "all 0.3s ease",
                          border: "2px solid #8B5FBF",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#8B5FBF";
                          e.target.style.color = "#fff";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "transparent";
                          e.target.style.color = "#8B5FBF";
                        }}
                      >
                        Login
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hero Section - Further reduced height */}
        <section
          className="section_hero"
          style={{
            position: "relative",
            minHeight: isLoggedIn ? "20vh" : "30vh", // Further reduced from 35vh to 30vh
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="/images/hero-poster.jpg"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center 30%", // Show upper portion for people/faces
              zIndex: 1,
            }}
          >
            <source src="/videos/hero-background.mp4" type="video/mp4" />
            <source src="/videos/hero-background.webm" type="video/webm" />
          </video>

          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, rgba(139, 95, 191, 0.3), rgba(107, 70, 193, 0.3)), linear-gradient(to bottom, transparent 40%, rgba(0, 0, 0, 0.1) 70%, rgba(0, 0, 0, 0.3) 100%)`,
              zIndex: 2,
            }}
          />

          <div className="padding-global padding-section-large" style={{ position: "relative", zIndex: 3, width: "100%" }}>
            <div className="container-large">
              <div className="hero_content text-align-center">
                <h1
                  className="hero-headline"
                  style={{
                    fontSize: "clamp(2rem, 5vw, 3.2rem)",
                    fontWeight: "800",
                    color: "#fff",
                    textShadow: "2px 2px 8px rgba(0, 0, 0, 0.6)",
                    lineHeight: "1.1",
                    maxWidth: "900px",
                    margin: "40px auto 16px",
                    textAlign: "center",
                  }}
                >
                  Meet new people
                </h1>

                <p style={{
                  color: "#fff",
                  fontSize: "clamp(1rem, 2.5vw, 1.25rem)",
                  textShadow: "1px 1px 4px rgba(0,0,0,0.5)",
                  margin: "0 auto 30px",
                  maxWidth: "600px",
                  textAlign: "center",
                  fontWeight: "500",
                }}>
                  Attend as many events as you like and get <strong>20% off food and drinks</strong> at the venue.
                </p>

                {/* Join Us button */}
                <div className="cta-buttons" style={{
                  marginTop: "10px",
                  marginBottom: "35px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}>
                  <a href="/events"
                    className="button-2 is-primary cta-button hero-cta"
                    style={buttonStyles}
                    onMouseEnter={(e) => {
                      Object.assign(e.target.style, buttonHoverStyles);
                    }}
                    onMouseLeave={(e) => {
                      Object.assign(e.target.style, buttonStyles);
                    }}
                  >
                    Join Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Membership Benefits Section */}
        <section
          style={{
            backgroundColor: "#FAFAFA",
            borderTop: "1px solid rgba(139, 95, 191, 0.1)",
            padding: "40px 20px",
          }}
        >
          <div style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}>
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "20px",
            }}>
              {[
                { icon: "🍹", title: "20% Off Food & Drinks", desc: "Show your member profile at the bar and enjoy 20% off food and drinks at the venue." },
                { icon: "🎉", title: "Unlimited Events", desc: "Attend as often as you like - your membership covers them all." },
                { icon: "🤝", title: "Members Only", desc: "Keeping every gathering friendly and welcoming." },
              ].map((item) => (
                <div
                  key={item.title}
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "16px",
                    padding: "28px 24px",
                    width: "220px",
                    boxShadow: "0 2px 12px rgba(139, 95, 191, 0.1)",
                    border: "1px solid rgba(139, 95, 191, 0.1)",
                  }}
                >
                  <div style={{ fontSize: "2.2rem", marginBottom: "12px" }}>{item.icon}</div>
                  <h4 style={{ fontSize: "1rem", fontWeight: "700", color: "#333", marginBottom: "8px" }}>{item.title}</h4>
                  <p style={{ fontSize: "0.88rem", color: "#666", margin: 0, lineHeight: "1.5" }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section - Only show if not logged in */}
        {!isLoggedIn && (
          <section
            style={{
              backgroundColor: "#fff",
              padding: "40px 20px",
              borderTop: "1px solid rgba(139, 95, 191, 0.1)",
            }}
          >
            <div style={{ maxWidth: "800px", margin: "0 auto" }}>
              <h2
                style={{
                  fontSize: "2rem",
                  fontWeight: "800",
                  background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  marginBottom: "40px",
                  textAlign: "center",
                }}
              >
                How It Works
              </h2>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0",
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                {[
                  { num: "1", title: "Become a Member", desc: "Must be a member to attend events.", icon: "✨" },
                  { num: "2", title: "Attend Events", desc: "Access all gatherings as many times as you like.", icon: "📅" },
                  { num: "3", title: "Get 20% Off Food & Drinks", desc: "Show your member profile at the bar and enjoy 20% off food and drinks at the venue.", icon: "🍹" },
                ].map((step, index, array) => (
                  <React.Fragment key={step.num}>
                    <div
                      style={{
                        backgroundColor: "#FAFAFA",
                        borderRadius: "16px",
                        padding: "32px 24px",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>{step.icon}</div>
                      <h3 style={{ fontSize: "1.2rem", fontWeight: "700", color: "#333", marginBottom: "8px" }}>{step.title}</h3>
                      <p style={{ fontSize: "0.95rem", color: "#666", margin: 0, lineHeight: "1.5" }}>{step.desc}</p>
                    </div>
                    {index < array.length - 1 && (
                      <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            backgroundColor: "#E9D5FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(139, 95, 191, 0.15)",
                          }}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 5V19M12 19L6 13M12 19L18 13" stroke="#8B5FBF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div style={{ textAlign: "center", marginTop: "40px" }}>
                <a
                  href="/events"
                  style={{
                    background: "linear-gradient(135deg, #8B5FBF, #2c2e96)",
                    color: "#fff",
                    padding: "18px 56px",
                    borderRadius: "50px",
                    textDecoration: "none",
                    fontSize: "1.1rem",
                    fontWeight: "700",
                    display: "inline-block",
                    boxShadow: "0 8px 30px rgba(139, 95, 191, 0.35)",
                  }}
                >
                  Join Us
                </a>
              </div>
            </div>
          </section>
        )}

        <Footer />

      </main>

      {/* Mobile Responsive Styles - Further reduced heights */}
      <style jsx>{`
@media (min-width: 1200px) {
  .section_hero {
    min-height: 50vh !important; // Further reduced from 60vh
    max-height: 600px !important; // Further reduced from 700px
  }
  
  .section_hero video {
    object-position: center 25% !important; // Show more of the top area
  }
}

@media (max-width: 1199px) and (min-width: 769px) {
  .section_hero {
    min-height: 45vh !important; // Further reduced from 55vh
  }
  
  .section_hero video {
    object-position: center 30% !important;
  }
}

@media (max-width: 768px) {
  .nav-content {
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .nav-signup-button, .nav-login-button {
    padding: 8px 16px !important;
    font-size: 0.85rem !important;
  }

  .section_hero {
    min-height: 40vh !important; // Further reduced from 50vh
    max-height: 350px !important; // Further reduced from 400px
  }
  
  .section_hero video {
    object-position: 40% 20% !important; // Focus on people, avoid edges
  }
  
  .hero-cta {
    padding: 22px 50px !important;
    font-size: 1.3rem !important;
    min-width: 260px !important;
  }
  
  .button-2.is-primary {
    padding: 16px 35px !important;
    font-size: 1.05rem !important;
  }
}

@media (max-width: 480px) {
  .nav-content {
    padding: 12px 0 !important;
  }
  
  .nav-logo {
    font-size: 1.4rem !important;
  }
  
  .nav-signup-button, .nav-login-button {
    padding: 6px 12px !important;
    font-size: 0.8rem !important;
  }

  .section_hero {
    min-height: 35vh !important; // Further reduced from 45vh
    max-height: 300px !important; // Further reduced from 350px
  }
  
  .section_hero video {
    object-position: 45% 15% !important; // Even more focused on center content
  }
  
  .hero-cta {
    padding: 20px 45px !important;
    font-size: 1.25rem !important;
    min-width: 240px !important;
  }
  
  .button-2.is-primary {
    padding: 14px 30px !important;
    font-size: 1rem !important;
  }
}

/* Add aspect ratio support for modern browsers */
@supports (aspect-ratio: 16 / 9) {
  .section_hero video {
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }
}
      `}</style>
    </div>
  );
};

export default Home;